<script setup lang="ts">
    import { reactive, onMounted, inject, provide, watch } from 'vue';
    import { loadForm } from './FormEditor.access';
    import type { Form, Theme, ToCopy } from './FormEditor.types';
    import { getHtml } from './FormEditor';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import content from './Content.vue';
    import contactlists from './ContactLists.vue';
    import formfields from './FormFields.vue';
    import FormRender from './FormRender.vue';
    import settings from './Settings.vue';
    import design from './Design.vue';
    import Modal from '@shared/components/Modal.vue';
    import EditorContainer from '@shared/components/Editor/EditorContainer.vue';
    import type { widgetRuleset } from '@views/Web/WidgetService.types';
    import { postWebContentChanges, getContent } from '@views/Web/Forms.access';
    import type { WebContent } from '@views/Web/Forms.types';
    import { copyToClipboard } from '@views/Web/copyToClipboard';
    import { bsAlert } from '@shared/components/bsAlerts';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import BSFormInputTextarea from '@shared/components/BSFormInputTextarea.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { richEditorCleanup } from '@shared/components/RichEditor/utils';
    import LinkEditor from '@shared/components/RichEditor/LinkEditor.vue';
    import ColorPickerSwatches from '@shared/components/ColorPickers/VACP/ColorPickerSwatches.vue';
    import ImageLibraryModal from '@shared/components/Images/ImageLibraryModal.vue';
    import { setupImageLibrary } from '@shared/components/Images/ImageLibrary';
    import { ImageLibraryProvide, imageLibraryProvideName } from '@shared/components/Images/types';
    import AiEditor from '@cwd/AiTools/AiEditor.vue';
    import { setupRichEditorProvides } from '@shared/components/RichEditor/setupProvides';
    import ThemeSelector from '@cwd/Themes/ThemeSelector.vue';
    import type { DesignTheme } from '@cwd/Themes/ThemesTypes';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const { aiEditor } = setupRichEditorProvides();

    const imageLibrary = setupImageLibrary();
    provide<ImageLibraryProvide>(imageLibraryProvideName, {
        state: imageLibrary.state,
        newSrc: imageLibrary.newSrc,
        insert: imageLibrary.insert,
        load: imageLibrary.load,
        toggleShowLibrary: imageLibrary.toggleShowLibrary,
        uploadFile: imageLibrary.uploadFile,
        uploadFromUrl: imageLibrary.uploadFromUrl,
        uploadFromBase64: imageLibrary.uploadFromBase64,
        navigateToAiEdit: imageLibrary.navigateToAiEdit,
    });

    const props = defineProps<{
        webContentId: string;
        copyFromWebContentId: string;
    }>();

    const ruleset = inject<widgetRuleset>('widgetRuleset');
    const defaultCurrentContent = { ContentType: 'Forms' } as WebContent;

    const state = reactive<{
        form: Form | null;
        htmlCss: string | null;
        html: string | null;
        js: string | null;
        viewHtml: boolean;
        currentContent: WebContent;
        saving: boolean;
        toCopy: ToCopy;
        selectedDesignTheme: DesignTheme | null;
    }>({
        form: null,
        htmlCss: null,
        html: null,
        js: null,
        viewHtml: false,
        currentContent: defaultCurrentContent,
        saving: false,
        toCopy: null,
        selectedDesignTheme: null,
    });

    const tempWebContentId = crypto.randomUUID();

    onMounted(async () => {
        initForm();

        watch(
            () => props.webContentId,
            (newVal) => {
                initForm();
            }
        );
    });

    const initForm = async () => {
        state.form = null; // reset

        if (props.webContentId || props.copyFromWebContentId) {
            const id = props.webContentId || props.copyFromWebContentId;
            const content = await getContent(id);

            state.currentContent = content[0];

            if (content[0].Data.cssVars === null) content[0].Data.cssVars = {}; // fix

            const form = content[0].Data;

            if (props.copyFromWebContentId) {
                content[0].WebContentId = '';
                content[0].Name = content[0].Name + ' [' + $Resources.CopyLabel + ']';
            }

            state.form = form;
        } else {
            // this it not how we should do it just testing to check how everything works
            state.currentContent = defaultCurrentContent;

            const data = await loadForm(0);
            const privacyUrl = contextAccess.state.currentAccount!.PrivacyUrl;

            data.consent = data.consent.replace('#', privacyUrl ?? '#');
            state.form = data;
            state.currentContent.Data = data;
        }
    };

    const viewHtml = () => {
        let result = getHtml(props.webContentId || tempWebContentId, state.form as Form, contextAccess.state.currentAccount);
        if (!result) return;

        state.htmlCss = result.htmlCss;
        state.html = result.html ? result.html : '';
        state.js = result.js ? result.js : '';
        state.toCopy = 'htmlCss';
        state.viewHtml = true;
    };

    const saveForm = async (): Promise<WebContent> => {
        state.saving = true;

        const content = state.currentContent;
        content.Data = state.form;
        content.Data.introduction = richEditorCleanup(content.Data.introduction);
        content.Data.consent = richEditorCleanup(content.Data.consent);

        content.Data.renderedHtml = getHtml(props.webContentId || tempWebContentId, state.form as Form, contextAccess.state.currentAccount).htmlCss;
        const savedContent = await postWebContentChanges(content);

        state.currentContent = savedContent;
        state.saving = false;

        bsAlert({ title: $Resources.FormSavedAlertHeading, content: $Resources.FormSavedAlertBody });

        if (!content.WebContentId) {
            // Update the URL in place without a router navigation: 'new' and 'edit/:webContentId'
            // are different route records, so router.replace() would unmount/remount FormEditor.
            history.replaceState(history.state, '', '/Web/Forms/edit/' + savedContent.WebContentId);
        }

        return savedContent;
    };

    const updateCssVars = (name: string, newValue: string) => {
        if (!state.form) return;
        state.form.cssVars[name] = newValue;
    };

    const updateTheme = (theme: Theme) => {
        if (state.form === null) return;

        for (var key in theme.cssVars) {
            state.form.cssVars[key] = theme.cssVars[key];
        }

        state.form.googleFonts = theme.googleFonts;
    };

    const copyCode = () => {
        switch (state.toCopy) {
            case 'htmlCss':
                return copyToClipboard(state.htmlCss!);
            case 'js':
                return copyToClipboard('<script>' + state.js + '</' + 'script>');
            default:
                return copyToClipboard(state.html!);
        }
    };

    const updateSelectedTheme = (themeId: string | null, designTheme: DesignTheme | null) => {
        if (!state.form) return;
        state.selectedDesignTheme = designTheme;
    };
</script>

<script lang="ts">
    export default { name: 'FormEditor' };
</script>

<template>
    <EditorContainer :show="state.form" :loading="!state.form" paneSide="right">
        <template #canvas>
            <FormRender
                v-if="state.form"
                :id="props.webContentId || tempWebContentId"
                :publicHost="contextAccess.state.currentAccount!.PublicEventUrl.PublicHost"
                :currentAccountId="contextAccess.state.currentAccount!.AccountId"
                :form="state.form"
                :selectedDesignTheme="state.selectedDesignTheme"></FormRender>
        </template>
        <template #paneNav>
            <h2 class="h4 m-0-all strong c-light">{{ $Resources.FormEditorName }}</h2>
            <button type="button" class="btn btn-default m-auto-l" @click="viewHtml">{{ $Resources.FormEditorViewHtml }}</button>
            <button type="button" class="btn btn-primary" @click="saveForm" :class="{ 'is-busy': state.saving }">
                {{ $Resources.FormEditorSaveForm }}
            </button>
        </template>
        <template #pane>
            <BSFormInputText
                class="form-group"
                v-model="state.currentContent.Name"
                :label="$Resources.FormEditorFormNameLabel"
                :placeholder="$Resources.FormEditorFormNamePlaceholder"></BSFormInputText>

            <BSFormInputTextarea
                class="form-group"
                v-model="state.currentContent.Description"
                :label="$Resources.FormEditorFormDescriptionLabel"
                :placeholder="$Resources.FormEditorFormDescriptionPlaceholder"></BSFormInputTextarea>

            <tabs navClass="tabs list-unstyled d-flex flex-justified text-center" v-if="state.form">
                <tab :title="$Resources.FormEditorTabContent">
                    <content
                        v-model:title="state.form.title"
                        v-model:introduction="state.form.introduction"
                        v-model:consent="state.form.consent"
                        v-model:button="state.form.button"
                        :image="state.form.image"
                        @update:image="(newValue: string) => (state.form!.image.src = newValue)"></content>
                </tab>
                <tab :title="$Resources.FormEditorTabLists">
                    <contactlists
                        v-model:listsDisplay="state.form.listsDisplay"
                        :listsLegend="state.form.listsLegend"
                        :lists="state.form.lists"
                        @update:listLegend="(newVal) => (state.form!.listsLegend! = newVal)"></contactlists>
                </tab>
                <tab :title="$Resources.SubsciptionFormsSubscriberInputsHeading">
                    <formfields :formFields="state.form.formFields"></formfields>
                </tab>
                <tab :title="$Resources.FormEditorTabSettings">
                    <settings
                        v-model:confirmationIssueId="state.form.confirmationIssueId"
                        v-model:doubleOptInIssueId="state.form.doubleOptInIssueId"
                        v-model:doubleOptInUrl="state.form.doubleOptInUrl"
                        v-model:subscriptionConfirmedUrl="state.form.subscriptionConfirmedUrl"
                        v-model:subscriptionFailedUrl="state.form.subscriptionFailedUrl"
                        v-model:defaultCountryCode="state.form.defaultCountryCode"
                        v-model:sourceName="state.form.sourceName"></settings>
                </tab>
                <tab :title="$Resources.FormEditorTabDesign">
                    <ThemeSelector
                        v-model:themeId="state.currentContent.Data.designThemeId"
                        v-model:useDefaultDesignTheme="state.currentContent.Data.useDefaultDesignTheme"
                        @update:selectedTheme="updateSelectedTheme" />

                    <design
                        v-if="!state.currentContent.Data.designThemeId && !state.currentContent.Data.useDefaultDesignTheme"
                        :cssVars="state.form.cssVars"
                        v-model:googleFonts="state.form.googleFonts"
                        @update:cssVars="updateCssVars"
                        @update:theme="updateTheme"></design>
                </tab>
            </tabs>
        </template>
    </EditorContainer>

    <div v-if="state.form">
        <Modal :title="$Resources.FormEditorViewHtml" size="lg" :scrollBody="true" :minHeight="true" :show="state.viewHtml" @close="state.viewHtml = false">
            <template #content>
                <p class="m-lg-b">{{ $Resources.FormEditorViewHtmlInfoText }}</p>
                <tabs>
                    <tab title="Html & CSS" @tabClick="state.toCopy = 'htmlCss'">
                        <pre>{{ state.htmlCss }}</pre>
                        <input type="text" :value="state.htmlCss" id="htmlCssToCopy" style="display: none" />
                    </tab>
                    <tab title="Html" @tabClick="state.toCopy = 'html'">
                        <pre>{{ state.html }}</pre>
                        <input type="text" :value="state.html" id="htmlToCopy" style="display: none" />
                    </tab>
                    <tab :title="$Resources.SubsciptionFormsJSHeading" @tabClick="state.toCopy = 'js'">
                        <pre>{{ '<script>' + state.js + '</script>' }}</pre>
                        <input type="text" :value="state.js" id="jsToCopy" style="display: none" />
                    </tab>
                </tabs>
            </template>
            <template #button>
                <button type="button" class="btn btn-primary" @click="copyCode">
                    {{ $Resources.CopyToClipBoardBtn }}
                </button>
            </template>
        </Modal>
    </div>

    <LinkEditor></LinkEditor>
    <ImageLibraryModal />
    <ColorPickerSwatches></ColorPickerSwatches>
    <AiEditor
        v-if="aiEditor"
        :getContent="aiEditor.getState.value.getContent"
        :show="aiEditor.getState.value.show"
        @update:content="(newValue: string) => aiEditor!.getState.value.updateContent(newValue)"
        @close="aiEditor.closeEditor"></AiEditor>
</template>

<style lang="less">
    .list-enter-active,
    .list-leave-active {
        transition:
            opacity 300ms ease-in-out,
            transform 300ms ease-in-out,
            background-color 300ms ease-in-out !important;
    }

    .list-enter-from,
    .list-leave-to {
        opacity: 0 !important;
        transform: scaleY(0);
        transform: scale(0.9) !important;
        transform-origin: center !important;
    }

    .list-enter-from {
        background-color: #1fa9b0 !important;
    }

    .list-leave-to {
        background-color: #999 !important;
    }

    .tabs.flex-justified > li > a {
        padding-left: 0;
        padding-right: 0;
    }

    .modal-scroll-body .modal-body {
        min-height: 70vh;
    }
</style>

<style scoped lang="less">
    pre {
        white-space: normal;
    }
</style>
